/* crm-dashboards-p3.jsx — Phase 3 Custom Analytics Dashboard
   Provides: CustomDashboardsView
   Self-registers: window.CustomDashboardsView
   Requires: react-grid-layout, recharts installed in node_modules */

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { ResponsiveGridLayout } from 'react-grid-layout';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Modal, EmptyState, ConfirmDialog } from '../../shared/ui/index.jsx';
import * as dashApi from '../../api/dashboards.api.js';
import * as aqApi  from '../../api/analytics-query.api.js';

const { useState, useEffect, useCallback, useRef, useMemo } = React;

// ─── Constants ───────────────────────────────────────────────────────────────

const GRID_COLS   = { lg: 24, md: 18, sm: 12, xs: 8, xxs: 6 };
const ROW_HEIGHT  = 80;
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };

const CHART_SUBTYPES = [
  { id: 'bar',    label: 'Bar Chart',   icon: 'BarChart2' },
  { id: 'line',   label: 'Line Chart',  icon: 'TrendingUp' },
  { id: 'pie',    label: 'Pie Chart',   icon: 'PieChart' },
  { id: 'metric', label: 'Metric',      icon: 'Hash' },
  { id: 'goal',   label: 'Goal',        icon: 'Target' },
  { id: 'table',  label: 'Table',       icon: 'Table' },
];

const WIDGET_TYPES = [
  { id: 'chart',    label: 'Chart',     icon: 'BarChart2',  desc: 'Visualize data with bar, line, pie, metric or goal charts' },
  { id: 'richtext', label: 'Rich Text', icon: 'FileText',   desc: 'Add markdown text, notes or instructions to your dashboard' },
  { id: 'iframe',   label: 'iFrame',    icon: 'Globe',      desc: 'Embed an external page or report by URL' },
];

const DEFAULT_SIZES = {
  bar: { w: 12, h: 5 }, line: { w: 12, h: 5 }, pie: { w: 8, h: 5 },
  metric: { w: 6, h: 3 }, goal: { w: 6, h: 4 }, table: { w: 16, h: 6 },
  richtext: { w: 8, h: 4 }, iframe: { w: 12, h: 6 },
};

const OPERATORS_BY_TYPE = {
  string:  ['eq','neq','contains','not_contains','starts_with','is_set','is_not_set','in','not_in'],
  number:  ['eq','neq','gt','gte','lt','lte','is_set','is_not_set','between'],
  boolean: ['eq','neq','is_set','is_not_set'],
  date:    ['eq','neq','gt','gte','lt','lte','is_set','is_not_set','between'],
  enum:    ['eq','neq','is_set','is_not_set','in','not_in'],
};

const OPERATOR_LABELS = {
  eq:'equals', neq:'not equals', gt:'greater than', gte:'≥', lt:'less than', lte:'≤',
  contains:'contains', not_contains:'not contains', starts_with:'starts with',
  is_set:'is set', is_not_set:'is not set', in:'in', not_in:'not in', between:'between',
};

const ACCENT_PALETTE = ['#125AB0','#0E8C5A','#B5740E','#7C3AED','#C2410C','#0369A1','#BE185D','#047857'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtNum = (v) => {
  const n = Number(v ?? 0);
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n % 1 === 0 ? n.toLocaleString() : n.toFixed(2);
};

const clamp = (v, mn, mx) => Math.max(mn, Math.min(mx, v));

function buildQuerySpec(config) {
  if (!config?.query?.source) return null;
  const q = config.query;
  const spec = {
    source: q.source,
    aggregations: (q.aggregations || []).filter(a => a.fn && a.alias),
    limit: clamp(q.limit || 500, 1, 5000),
  };
  if (q.filters?.conditions?.length) spec.filters = q.filters;
  if (q.groupBy?.length) spec.groupBy = q.groupBy.filter(g => g.field);
  if (q.sort?.length) spec.sort = q.sort;
  if (!spec.aggregations.length) return null;
  return spec;
}

function ensureFilters(f) {
  if (!f) return { logic: 'AND', conditions: [] };
  if (!f.conditions) return { logic: f.logic || 'AND', conditions: [] };
  return f;
}

// ─── Spinner ─────────────────────────────────────────────────────────────────

const Spinner = ({ size = 18 }) => (
  <div className="nx-aq-spinner" style={{ width: size, height: size }}>
    <NexusIcon name="Loader" size={size} className="nx-aq-spin" />
  </div>
);

// ─── Chart Renderers ─────────────────────────────────────────────────────────

const COLORS = ['#125AB0','#0E8C5A','#7C3AED','#B5740E','#C2410C','#0369A1','#BE185D','#047857','#4692EC','#34C188'];

function pickCol(cfg, idx) {
  const cols = cfg?.visualization?.colors;
  if (cols?.length) return cols[idx % cols.length];
  return COLORS[idx % COLORS.length];
}

const BarRenderer = ({ result, config }) => {
  if (!result?.rows?.length) return <div className="nx-aq-nodata">No data</div>;
  const vis = config?.visualization ?? {};
  const xKey = result.columns.find(c => !c.isAggregation)?.key;
  const yKeys = result.columns.filter(c => c.isAggregation).map(c => c.key);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={result.rows} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--nx-border)" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: 'var(--nx-text-muted)' }} />
        <YAxis tick={{ fontSize: 11, fill: 'var(--nx-text-muted)' }} width={50} tickFormatter={fmtNum} />
        <Tooltip formatter={(v) => fmtNum(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--nx-border)', background: 'var(--nx-surface)' }} />
        {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {yKeys.map((k, i) => (
          <Bar key={k} dataKey={k} fill={pickCol(config, i)} radius={[3,3,0,0]}
            label={vis.dataLabels ? { position: 'top', fontSize: 10, formatter: fmtNum } : false} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

const LineRenderer = ({ result, config }) => {
  if (!result?.rows?.length) return <div className="nx-aq-nodata">No data</div>;
  const xKey = result.columns.find(c => !c.isAggregation)?.key;
  const yKeys = result.columns.filter(c => c.isAggregation).map(c => c.key);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={result.rows} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--nx-border)" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: 'var(--nx-text-muted)' }} />
        <YAxis tick={{ fontSize: 11, fill: 'var(--nx-text-muted)' }} width={50} tickFormatter={fmtNum} />
        <Tooltip formatter={(v) => fmtNum(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--nx-border)', background: 'var(--nx-surface)' }} />
        {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {yKeys.map((k, i) => (
          <Line key={k} type="monotone" dataKey={k} stroke={pickCol(config, i)} strokeWidth={2} dot={{ r: 3 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

const PieRenderer = ({ result, config }) => {
  if (!result?.rows?.length) return <div className="nx-aq-nodata">No data</div>;
  const nameKey = result.columns.find(c => !c.isAggregation)?.key;
  const valKey  = result.columns.find(c => c.isAggregation)?.key;
  const data = result.rows.map(r => ({ name: String(r[nameKey] ?? '—'), value: Number(r[valKey] ?? 0) }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="70%"
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
          {data.map((_, i) => <Cell key={i} fill={pickCol(config, i)} />)}
        </Pie>
        <Tooltip formatter={(v) => fmtNum(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--nx-border)', background: 'var(--nx-surface)' }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

const TableRenderer = ({ result }) => {
  if (!result?.rows?.length) return <div className="nx-aq-nodata">No data</div>;
  return (
    <div style={{ overflow: 'auto', height: '100%' }}>
      <table className="nx-table nx-aq-table">
        <thead>
          <tr>{result.columns.map(c => <th key={c.key}>{c.label || c.key}</th>)}</tr>
        </thead>
        <tbody>
          {result.rows.map((row, i) => (
            <tr key={i}>
              {result.columns.map(c => (
                <td key={c.key}>
                  {c.isAggregation ? fmtNum(row[c.key]) : String(row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MetricRenderer = ({ result, config }) => {
  const aggCol = result?.columns?.find(c => c.isAggregation);
  const val    = result?.rows?.[0]?.[aggCol?.key];
  const label  = config?.visualization?.axisName || aggCol?.label || 'Value';
  return (
    <div className="nx-aq-metric">
      <div className="nx-aq-metric-val">{val != null ? fmtNum(val) : '—'}</div>
      <div className="nx-aq-metric-label">{label}</div>
      {result?.metadata?.durationMs != null && (
        <div className="nx-aq-metric-sub">from {result.metadata.source}</div>
      )}
    </div>
  );
};

const GoalRenderer = ({ result, config }) => {
  const aggCol = result?.columns?.find(c => c.isAggregation);
  const val    = Number(result?.rows?.[0]?.[aggCol?.key] ?? 0);
  const target = Number(config?.visualization?.goalTarget || 100);
  const pct    = target > 0 ? Math.min(100, Math.round((val / target) * 100)) : 0;
  const label  = config?.visualization?.axisName || aggCol?.label || 'Progress';
  const color  = pct >= 100 ? 'var(--nx-success)' : pct >= 50 ? 'var(--nx-accent)' : 'var(--nx-warning)';
  return (
    <div className="nx-aq-goal">
      <div className="nx-aq-goal-ring">
        <RingMeter value={pct} size={88} stroke={8} color={color} label={`${pct}%`} />
      </div>
      <div className="nx-aq-goal-info">
        <div className="nx-aq-goal-val">{fmtNum(val)}</div>
        <div className="nx-aq-goal-label">{label}</div>
        <div className="nx-aq-goal-target">Target: {fmtNum(target)}</div>
      </div>
    </div>
  );
};

const RichTextContent = ({ config }) => {
  const content = config?.content || '*No content yet.*';
  return (
    <div className="nx-aq-richtext">
      {content.split('\n').map((line, i) => {
        if (line.startsWith('## ')) return <h3 key={i} className="nx-aq-rt-h3">{line.slice(3)}</h3>;
        if (line.startsWith('# '))  return <h2 key={i} className="nx-aq-rt-h2">{line.slice(2)}</h2>;
        if (line.startsWith('**') && line.endsWith('**')) return <strong key={i}>{line.slice(2,-2)}</strong>;
        if (line === '') return <br key={i} />;
        return <p key={i} style={{ margin: '2px 0', fontSize: 13 }}>{line}</p>;
      })}
    </div>
  );
};

const IFrameContent = ({ config }) => {
  const url = config?.url;
  if (!url) return <div className="nx-aq-nodata">No URL configured</div>;
  return (
    <iframe
      src={url}
      className="nx-aq-iframe"
      title="Embedded content"
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    />
  );
};

// ─── Widget Content (query executor + renderer) ───────────────────────────────

const WidgetContent = ({ widget, workspaceId }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const abortRef = useRef(null);

  const { widgetType, config } = widget;

  const runQuery = useCallback(async () => {
    if (widgetType !== 'chart') return;
    const spec = buildQuerySpec(config);
    if (!spec) { setResult(null); return; }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setErr(null);
    try {
      const r = await aqApi.executeQuery(workspaceId, spec);
      if (!ctrl.signal.aborted) { setResult(r); setLoading(false); }
    } catch (e) {
      if (!ctrl.signal.aborted) { setErr(e.message || 'Query failed'); setLoading(false); }
    }
  }, [widgetType, workspaceId, JSON.stringify(config?.query)]);

  useEffect(() => { runQuery(); return () => abortRef.current?.abort(); }, [runQuery]);

  if (widgetType === 'richtext') return <RichTextContent config={config} />;
  if (widgetType === 'iframe')   return <IFrameContent config={config} />;

  if (loading) return <div className="nx-aq-state"><Spinner /> <span>Loading…</span></div>;
  if (err)     return <div className="nx-aq-state nx-aq-err"><NexusIcon name="AlertCircle" size={16} /><span>{err}</span></div>;
  if (!result) return <div className="nx-aq-state nx-aq-nodata-msg">Configure this widget to show data</div>;

  const sub = config?.chartSubtype || 'bar';
  if (sub === 'bar')    return <BarRenderer    result={result} config={config} />;
  if (sub === 'line')   return <LineRenderer   result={result} config={config} />;
  if (sub === 'pie')    return <PieRenderer    result={result} config={config} />;
  if (sub === 'table')  return <TableRenderer  result={result} />;
  if (sub === 'metric') return <MetricRenderer result={result} config={config} />;
  if (sub === 'goal')   return <GoalRenderer   result={result} config={config} />;
  return <div className="nx-aq-nodata">Unknown chart type</div>;
};

// ─── Widget Card ─────────────────────────────────────────────────────────────

const WidgetCard = ({ widget, workspaceId, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const subType = widget.config?.chartSubtype;
  const typeInfo = widget.widgetType === 'chart' && subType
    ? CHART_SUBTYPES.find(t => t.id === subType)
    : WIDGET_TYPES.find(t => t.id === widget.widgetType);

  return (
    <div className="nx-aq-card">
      <div className="nx-aq-card-head">
        <div className="nx-aq-card-title">
          {typeInfo && <NexusIcon name={typeInfo.icon} size={12} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />}
          <span>{widget.title || 'Untitled Widget'}</span>
        </div>
        <div className="nx-aq-card-actions" ref={menuRef}>
          <button type="button" className="nx-iconbtn nx-aq-menu-btn" onClick={() => setMenuOpen(v => !v)} title="Widget options">
            <NexusIcon name="MoreHorizontal" size={14} />
          </button>
          {menuOpen && (
            <div className="nx-aq-menu">
              <button className="nx-aq-menu-item" onClick={() => { setMenuOpen(false); onEdit(widget); }}>
                <NexusIcon name="Pencil" size={13} /> Edit
              </button>
              <button className="nx-aq-menu-item nx-aq-menu-danger" onClick={() => { setMenuOpen(false); onDelete(widget); }}>
                <NexusIcon name="Trash2" size={13} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="nx-aq-card-body">
        <WidgetContent widget={widget} workspaceId={workspaceId} />
      </div>
    </div>
  );
};

// ─── Widget Grid ─────────────────────────────────────────────────────────────

const WidgetGrid = ({ widgets, workspaceId, dashboardId, onEdit, onDelete, onLayoutChange }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width;
      if (w) setContainerWidth(Math.floor(w));
    });
    obs.observe(containerRef.current);
    setContainerWidth(containerRef.current.offsetWidth || 1200);
    return () => obs.disconnect();
  }, []);

  const layout = widgets.map(w => ({
    i: w.id,
    x: w.position?.x ?? 0,
    y: w.position?.y ?? 0,
    w: w.position?.w ?? 6,
    h: w.position?.h ?? 4,
    minW: 3, minH: 2,
  }));

  if (!widgets.length) {
    return (
      <div className="nx-aq-empty-tab">
        <EmptyState icon="LayoutDashboard" title="No widgets yet" desc="Click + Add Widget to build your dashboard." />
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <ResponsiveGridLayout
        className="nx-aq-grid"
        width={containerWidth}
        layouts={{ lg: layout, md: layout, sm: layout }}
        cols={GRID_COLS}
        rowHeight={ROW_HEIGHT}
        breakpoints={BREAKPOINTS}
        draggableHandle=".nx-aq-card-head"
        onLayoutChange={(lay) => onLayoutChange(lay)}
        margin={[10, 10]}
        containerPadding={[0, 0]}
        isResizable
        isDraggable
        resizeHandles={['se']}
      >
        {widgets.map(w => (
          <div key={w.id}>
            <WidgetCard widget={w} workspaceId={workspaceId} onEdit={onEdit} onDelete={onDelete} />
          </div>
        ))}
      </ResponsiveGridLayout>
    </div>
  );
};

// ─── Filter Builder ───────────────────────────────────────────────────────────

const ConditionRow = ({ cond, fields, index, onChange, onRemove }) => {
  const field = fields.find(f => f.key === cond.field);
  const ops   = field ? (OPERATORS_BY_TYPE[field.type] || OPERATORS_BY_TYPE.string) : [];
  const needsValue = !['is_set','is_not_set'].includes(cond.op);
  const needsValue2 = cond.op === 'between';

  const ValueInput = () => {
    if (!needsValue) return null;
    if (field?.type === 'enum' && field.enumValues?.length) {
      if (cond.op === 'in' || cond.op === 'not_in') {
        const selected = Array.isArray(cond.value) ? cond.value : [];
        return (
          <div className="nx-aq-enum-multi">
            {field.enumValues.map(v => (
              <label key={v} className="nx-aq-enum-chip">
                <input type="checkbox" checked={selected.includes(v)}
                  onChange={e => onChange('value', e.target.checked ? [...selected, v] : selected.filter(x => x !== v))} />
                {v}
              </label>
            ))}
          </div>
        );
      }
      return (
        <select className="nx-input nx-aq-cond-val" value={cond.value ?? ''} onChange={e => onChange('value', e.target.value)}>
          <option value="">— value —</option>
          {field.enumValues.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      );
    }
    if (field?.type === 'boolean') {
      return (
        <select className="nx-input nx-aq-cond-val" value={cond.value ?? ''} onChange={e => onChange('value', e.target.value === 'true')}>
          <option value="">— value —</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }
    if (field?.type === 'date') {
      return (
        <>
          <input type="date" className="nx-input nx-aq-cond-val" value={cond.value ?? ''} onChange={e => onChange('value', e.target.value)} />
          {needsValue2 && <input type="date" className="nx-input nx-aq-cond-val" value={cond.value2 ?? ''} onChange={e => onChange('value2', e.target.value)} />}
        </>
      );
    }
    if (field?.type === 'number') {
      return (
        <>
          <input type="number" className="nx-input nx-aq-cond-val" placeholder="value" value={cond.value ?? ''} onChange={e => onChange('value', Number(e.target.value))} />
          {needsValue2 && <input type="number" className="nx-input nx-aq-cond-val" placeholder="to" value={cond.value2 ?? ''} onChange={e => onChange('value2', Number(e.target.value))} />}
        </>
      );
    }
    return <input type="text" className="nx-input nx-aq-cond-val" placeholder="value" value={cond.value ?? ''} onChange={e => onChange('value', e.target.value)} />;
  };

  return (
    <div className="nx-aq-cond-row">
      <select className="nx-input nx-aq-cond-field" value={cond.field ?? ''} onChange={e => onChange('field', e.target.value)}>
        <option value="">— field —</option>
        {fields.filter(f => f.filterable).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
      </select>
      <select className="nx-input nx-aq-cond-op" value={cond.op ?? ''} onChange={e => onChange('op', e.target.value)}>
        <option value="">— op —</option>
        {ops.map(op => <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>)}
      </select>
      <ValueInput />
      <button type="button" className="nx-iconbtn" onClick={onRemove} title="Remove filter">
        <NexusIcon name="X" size={13} />
      </button>
    </div>
  );
};

const FilterBuilder = ({ filters, fields, onChange }) => {
  const grp = ensureFilters(filters);

  const addCond = () => onChange({ ...grp, conditions: [...grp.conditions, { field: '', op: 'eq', value: '' }] });
  const removeCond = (i) => onChange({ ...grp, conditions: grp.conditions.filter((_, idx) => idx !== i) });
  const updateCond = (i, key, val) => {
    const next = grp.conditions.map((c, idx) => idx === i ? { ...c, [key]: val } : c);
    onChange({ ...grp, conditions: next });
  };

  return (
    <div className="nx-aq-filter-builder">
      <div className="nx-aq-filter-head">
        <span className="nx-aq-filter-label">Where</span>
        <select className="nx-input" style={{ width: 70, fontSize: 11 }} value={grp.logic}
          onChange={e => onChange({ ...grp, logic: e.target.value })}>
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
        <button type="button" className="nx-aq-add-cond" onClick={addCond}>
          <NexusIcon name="Plus" size={12} /> Add filter
        </button>
      </div>
      {grp.conditions.map((c, i) => (
        <ConditionRow key={i} cond={c} fields={fields} index={i}
          onChange={(k, v) => updateCond(i, k, v)}
          onRemove={() => removeCond(i)} />
      ))}
      {!grp.conditions.length && (
        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', padding: '4px 0' }}>No filters — all records included</div>
      )}
    </div>
  );
};

// ─── Preview Panel ────────────────────────────────────────────────────────────

const PreviewPanel = ({ config, workspaceId }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]   = useState(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  const specJson = JSON.stringify(buildQuerySpec(config));

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const spec = buildQuerySpec(config);
      if (!spec) { setResult(null); setErr(null); return; }
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true); setErr(null);
      try {
        const r = await aqApi.executeQuery(workspaceId, spec);
        if (!ctrl.signal.aborted) { setResult(r); setLoading(false); }
      } catch (e) {
        if (!ctrl.signal.aborted) { setErr(e.message || 'Query failed'); setLoading(false); }
      }
    }, 600);
    return () => { clearTimeout(timerRef.current); abortRef.current?.abort(); };
  }, [specJson, workspaceId]);

  const previewWidget = { ...({} || {}), widgetType: 'chart', config };

  return (
    <div className="nx-aq-preview">
      <div className="nx-aq-preview-head">
        <NexusIcon name="Eye" size={13} />
        <span>Preview</span>
        {loading && <Spinner size={13} />}
      </div>
      <div className="nx-aq-preview-body">
        {err ? (
          <div className="nx-aq-err-msg"><NexusIcon name="AlertCircle" size={14} /><span>{err}</span></div>
        ) : !result ? (
          <div className="nx-aq-nodata">Configure source and aggregation to see a preview</div>
        ) : (
          <WidgetContent widget={previewWidget} workspaceId={workspaceId} />
        )}
      </div>
      {result && (
        <div className="nx-aq-preview-meta">
          {result.total} row{result.total !== 1 ? 's' : ''}
          {result.truncated && <span className="nx-aq-trunc"> · truncated</span>}
          <span style={{ marginLeft: 'auto', color: 'var(--nx-text-muted)' }}>{result.metadata?.durationMs}ms</span>
        </div>
      )}
    </div>
  );
};

// ─── Widget Editor Modal ──────────────────────────────────────────────────────

const EDITOR_TABS = ['Data', 'Visualization', 'Preview'];

const WidgetEditor = ({ initial, workspaceId, sources, onSave, onClose, isSaving, saveError }) => {
  const isNew = !initial?.id;
  const [activeTab, setActiveTab] = useState('Data');
  const [widgetType, setWidgetType] = useState(initial?.widgetType || 'chart');
  const [title, setTitle] = useState(initial?.title || '');
  const [chartSubtype, setChartSubtype] = useState(initial?.config?.chartSubtype || 'bar');
  const [source, setSource] = useState(initial?.config?.query?.source || '');
  const [fields, setFields] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [filters, setFilters] = useState(ensureFilters(initial?.config?.query?.filters));
  const [groupBy, setGroupBy] = useState(initial?.config?.query?.groupBy || []);
  const [aggregations, setAggregations] = useState(initial?.config?.query?.aggregations || [{ fn: 'count', alias: 'count', field: null }]);
  const [sort, setSort] = useState(initial?.config?.query?.sort || []);
  const [goalTarget, setGoalTarget] = useState(initial?.config?.visualization?.goalTarget || '');
  const [axisName, setAxisName] = useState(initial?.config?.visualization?.axisName || '');
  const [dataLabels, setDataLabels] = useState(initial?.config?.visualization?.dataLabels || false);
  const [colors, setColors] = useState(initial?.config?.visualization?.colors || []);
  const [richContent, setRichContent] = useState(initial?.config?.content || '');
  const [iframeUrl, setIframeUrl] = useState(initial?.config?.url || '');
  const [limit, setLimit] = useState(initial?.config?.query?.limit || 500);

  useEffect(() => {
    if (!source) { setFields([]); return; }
    setSchemaLoading(true);
    aqApi.getSchema(workspaceId, source)
      .then(s => {
        const src = Array.isArray(s?.sources) ? s.sources[0] : s;
        setFields(src?.fields || []);
      })
      .catch(() => setFields([]))
      .finally(() => setSchemaLoading(false));
  }, [source, workspaceId]);

  const groupableFields = fields.filter(f => f.groupable);
  const aggFields       = fields.filter(f => f.aggregatable);
  const sortableFields  = fields.filter(f => f.sortable);

  const buildConfig = () => {
    if (widgetType === 'richtext') return { type: 'richtext', content: richContent };
    if (widgetType === 'iframe')   return { type: 'iframe', url: iframeUrl };
    return {
      type: 'chart',
      chartSubtype,
      query: {
        source, limit,
        filters: filters.conditions.length ? filters : undefined,
        groupBy: groupBy.length ? groupBy : undefined,
        aggregations,
        sort: sort.length ? sort : undefined,
      },
      visualization: {
        axisName: axisName || undefined,
        goalTarget: goalTarget ? Number(goalTarget) : undefined,
        dataLabels,
        colors: colors.length ? colors : undefined,
      },
    };
  };

  const handleSave = () => {
    const cfg = buildConfig();
    const pos = initial?.position ?? (DEFAULT_SIZES[widgetType === 'chart' ? chartSubtype : widgetType] || { w: 8, h: 4 });
    onSave({
      id: initial?.id,
      title: title || 'Untitled Widget',
      widgetType,
      config: cfg,
      position: initial?.position || { x: 0, y: 999, ...pos },
      tabId: initial?.tabId,
    });
  };

  const curConfig = buildConfig();

  const addAgg = () => setAggregations(a => [...a, { fn: 'count', alias: `agg_${a.length + 1}`, field: null }]);
  const removeAgg = (i) => setAggregations(a => a.filter((_, idx) => idx !== i));
  const updateAgg = (i, patch) => setAggregations(a => a.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const addGroupBy = () => setGroupBy(g => [...g, { field: '' }]);
  const removeGroupBy = (i) => setGroupBy(g => g.filter((_, idx) => idx !== i));
  const updateGroupBy = (i, patch) => setGroupBy(g => g.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  const isChartType = widgetType === 'chart';
  const showGroupBy = isChartType && !['metric', 'goal'].includes(chartSubtype);

  return (
    <Modal title={isNew ? 'Add Widget' : 'Edit Widget'} icon="Sliders" onClose={onClose} width={860}
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {saveError && (
            <div style={{ padding: '7px 12px', background: 'var(--nx-danger-bg, #fef2f2)', border: '1px solid var(--nx-danger-border, #fecaca)', borderRadius: 6, color: 'var(--nx-danger, #dc2626)', fontSize: 12 }}>
              {saveError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : isNew ? 'Add Widget' : 'Save Changes'}
            </Btn>
          </div>
        </div>
      }>
      <div className="nx-aq-editor">
        {/* Left: config panel */}
        <div className="nx-aq-editor-left">
          {/* Title */}
          <div className="nx-aq-editor-section">
            <label className="nx-aq-editor-label">Title</label>
            <input className="nx-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Widget title…" />
          </div>

          {/* Widget type tabs */}
          <div className="nx-aq-editor-section">
            <label className="nx-aq-editor-label">Type</label>
            <div className="nx-aq-type-btns">
              {WIDGET_TYPES.map(t => (
                <button key={t.id} type="button"
                  className={'nx-aq-type-btn' + (widgetType === t.id ? ' is-active' : '')}
                  onClick={() => setWidgetType(t.id)}>
                  <NexusIcon name={t.icon} size={14} />
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Rich text editor */}
          {widgetType === 'richtext' && (
            <div className="nx-aq-editor-section">
              <label className="nx-aq-editor-label">Content (Markdown)</label>
              <textarea className="nx-input nx-aq-rt-editor" rows={10} value={richContent}
                onChange={e => setRichContent(e.target.value)} placeholder="## Title&#10;Write markdown here…" />
            </div>
          )}

          {/* iFrame */}
          {widgetType === 'iframe' && (
            <div className="nx-aq-editor-section">
              <label className="nx-aq-editor-label">URL</label>
              <input className="nx-input" value={iframeUrl} onChange={e => setIframeUrl(e.target.value)} placeholder="https://…" />
              <p className="nx-aq-editor-hint">The URL must be accessible from the user's browser.</p>
            </div>
          )}

          {/* Chart config */}
          {widgetType === 'chart' && (
            <>
              <div className="nx-aq-editor-section">
                <label className="nx-aq-editor-label">Chart Subtype</label>
                <div className="nx-aq-subtype-btns">
                  {CHART_SUBTYPES.map(t => (
                    <button key={t.id} type="button"
                      className={'nx-aq-subtype-btn' + (chartSubtype === t.id ? ' is-active' : '')}
                      onClick={() => setChartSubtype(t.id)} title={t.label}>
                      <NexusIcon name={t.icon} size={14} />
                      <span>{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Source */}
              <div className="nx-aq-editor-section">
                <label className="nx-aq-editor-label">Source</label>
                <select className="nx-input" value={source} onChange={e => { setSource(e.target.value); setGroupBy([]); setAggregations([{ fn: 'count', alias: 'count', field: null }]); setFilters(ensureFilters()); }}>
                  <option value="">— select source —</option>
                  {sources.map(s => <option key={s.source} value={s.source}>{s.displayLabel}</option>)}
                </select>
              </div>

              {/* Filters */}
              {source && (
                <div className="nx-aq-editor-section">
                  <label className="nx-aq-editor-label">Filters</label>
                  {schemaLoading ? <Spinner /> : (
                    <FilterBuilder filters={filters} fields={fields} onChange={setFilters} />
                  )}
                </div>
              )}

              {/* GroupBy (X Axis) */}
              {source && showGroupBy && (
                <div className="nx-aq-editor-section">
                  <label className="nx-aq-editor-label">Group By (X Axis)</label>
                  {groupBy.map((gb, i) => (
                    <div key={i} className="nx-aq-axis-row">
                      <select className="nx-input nx-aq-axis-field" value={gb.field}
                        onChange={e => updateGroupBy(i, { field: e.target.value })}>
                        <option value="">— field —</option>
                        {groupableFields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </select>
                      {fields.find(f => f.key === gb.field)?.supportsDateGranularity && (
                        <select className="nx-input" style={{ width: 100 }} value={gb.dateGranularity || ''}
                          onChange={e => updateGroupBy(i, { dateGranularity: e.target.value || undefined })}>
                          <option value="">granularity</option>
                          {['day','week','month','quarter','year'].map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      )}
                      <button type="button" className="nx-iconbtn" onClick={() => removeGroupBy(i)}>
                        <NexusIcon name="X" size={13} />
                      </button>
                    </div>
                  ))}
                  {groupBy.length < 5 && (
                    <button type="button" className="nx-aq-add-cond" onClick={addGroupBy}>
                      <NexusIcon name="Plus" size={12} /> Add group
                    </button>
                  )}
                </div>
              )}

              {/* Aggregations (Y Axis) */}
              {source && (
                <div className="nx-aq-editor-section">
                  <label className="nx-aq-editor-label">Aggregations (Y Axis)</label>
                  {aggregations.map((agg, i) => {
                    const fn = agg.fn;
                    const needsField = fn !== 'count';
                    const fieldDef  = fields.find(f => f.key === agg.field);
                    const validFns  = needsField && fieldDef ? fieldDef.supportedAggFns : ['count','count_distinct','sum','avg','min','max'];
                    return (
                      <div key={i} className="nx-aq-agg-row">
                        <select className="nx-input" style={{ width: 120 }} value={fn}
                          onChange={e => updateAgg(i, { fn: e.target.value, field: e.target.value === 'count' ? null : agg.field })}>
                          {['count','count_distinct','sum','avg','min','max'].map(f => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                        {needsField && (
                          <select className="nx-input" style={{ flex: 1 }} value={agg.field || ''}
                            onChange={e => updateAgg(i, { field: e.target.value })}>
                            <option value="">— field —</option>
                            {aggFields.filter(f => f.supportedAggFns.includes(fn)).map(f => (
                              <option key={f.key} value={f.key}>{f.label}</option>
                            ))}
                          </select>
                        )}
                        <input className="nx-input" style={{ width: 90 }} placeholder="alias" value={agg.alias}
                          onChange={e => updateAgg(i, { alias: e.target.value })} />
                        {aggregations.length > 1 && (
                          <button type="button" className="nx-iconbtn" onClick={() => removeAgg(i)}>
                            <NexusIcon name="X" size={13} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {aggregations.length < 10 && (
                    <button type="button" className="nx-aq-add-cond" onClick={addAgg}>
                      <NexusIcon name="Plus" size={12} /> Add aggregation
                    </button>
                  )}
                </div>
              )}

              {/* Goal target */}
              {chartSubtype === 'goal' && (
                <div className="nx-aq-editor-section">
                  <label className="nx-aq-editor-label">Goal Target</label>
                  <input type="number" className="nx-input" value={goalTarget} onChange={e => setGoalTarget(e.target.value)} placeholder="e.g. 100" />
                </div>
              )}

              {/* Style */}
              <div className="nx-aq-editor-section">
                <label className="nx-aq-editor-label">Style</label>
                <input className="nx-input" value={axisName} onChange={e => setAxisName(e.target.value)} placeholder="Axis / metric label (optional)" style={{ marginBottom: 6 }} />
                {!['metric','goal'].includes(chartSubtype) && (
                  <label className="nx-aq-checkbox-row">
                    <input type="checkbox" checked={dataLabels} onChange={e => setDataLabels(e.target.checked)} />
                    Show data labels
                  </label>
                )}
              </div>

              {/* Limit */}
              <div className="nx-aq-editor-section">
                <label className="nx-aq-editor-label">Row Limit</label>
                <input type="number" className="nx-input" value={limit} min={1} max={5000}
                  onChange={e => setLimit(Math.min(5000, Math.max(1, Number(e.target.value))))} />
              </div>
            </>
          )}
        </div>

        {/* Right: preview */}
        {widgetType === 'chart' && source && (
          <div className="nx-aq-editor-right">
            <PreviewPanel config={curConfig} workspaceId={workspaceId} />
          </div>
        )}
      </div>
    </Modal>
  );
};

// ─── Add Widget Modal ─────────────────────────────────────────────────────────

const AddWidgetModal = ({ onSelect, onClose }) => (
  <Modal title="Add Widget" icon="LayoutDashboard" onClose={onClose} width={520}>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, padding: '4px 0 8px' }}>
      {WIDGET_TYPES.map(t => (
        <button key={t.id} type="button" className="nx-aq-add-type-card" onClick={() => onSelect(t.id)}>
          <NexusIcon name={t.icon} size={24} style={{ color: 'var(--nx-accent)', marginBottom: 8 }} />
          <div style={{ fontWeight: 700, fontSize: 13 }}>{t.label}</div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4, lineHeight: 1.4 }}>{t.desc}</div>
        </button>
      ))}
    </div>
  </Modal>
);

// ─── Dashboard Tab Bar ────────────────────────────────────────────────────────

const DashboardTabBar = ({ tabs, activeTabId, onSwitch, onAdd, onRename, onDelete }) => {
  const [editingTabId, setEditingTabId] = useState(null);
  const [editValue, setEditValue]       = useState('');
  const inputRef = useRef(null);

  const startEdit = (tab) => {
    setEditingTabId(tab.id);
    setEditValue(tab.title);
    setTimeout(() => inputRef.current?.focus(), 50);
  };
  const commitEdit = (tabId) => {
    if (editValue.trim()) onRename(tabId, editValue.trim());
    setEditingTabId(null);
  };

  return (
    <div className="nx-aq-tabbar">
      <div className="nx-tabs" style={{ flex: 1, overflow: 'hidden' }}>
        {tabs.map(tab => (
          <button key={tab.id}
            className={'nx-tab' + (tab.id === activeTabId ? ' is-active' : '')}
            onClick={() => onSwitch(tab.id)}
            onDoubleClick={() => startEdit(tab)}>
            {editingTabId === tab.id ? (
              <input ref={inputRef} className="nx-aq-tab-edit" value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onBlur={() => commitEdit(tab.id)}
                onKeyDown={e => { if (e.key === 'Enter') commitEdit(tab.id); if (e.key === 'Escape') setEditingTabId(null); }}
                onClick={e => e.stopPropagation()} />
            ) : tab.title}
          </button>
        ))}
        <button type="button" className="nx-aq-add-tab" onClick={onAdd} title="Add tab">
          <NexusIcon name="Plus" size={13} />
        </button>
      </div>
      {tabs.length > 1 && activeTabId && (
        <button type="button" className="nx-aq-del-tab" onClick={() => onDelete(activeTabId)} title="Delete tab">
          <NexusIcon name="Trash2" size={13} />
        </button>
      )}
    </div>
  );
};

// ─── Dashboard Shell ──────────────────────────────────────────────────────────

const DashboardShell = ({ dashboard, workspaceId, onChange }) => {
  const [tabs, setTabs]         = useState(dashboard.tabs ?? []);
  const [activeTabId, setActiveTabId] = useState(dashboard.tabs?.[0]?.id ?? null);
  const [showAdd, setShowAdd]   = useState(false);
  const [editing, setEditing]   = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [sources, setSources]   = useState([]);
  const [layoutBusy, setLayoutBusy] = useState(false);
  const layoutTimer = useRef(null);

  useEffect(() => {
    aqApi.getSchema(workspaceId).then(r => {
      const list = Array.isArray(r) ? r : (r?.sources ?? []);
      setSources(list);
    }).catch(() => {});
  }, [workspaceId]);

  // Always sync tabs from dashboard prop when it changes
  useEffect(() => {
    setTabs(dashboard.tabs ?? []);
    if (!dashboard.tabs?.find(t => t.id === activeTabId)) {
      setActiveTabId(dashboard.tabs?.[0]?.id ?? null);
    }
  }, [dashboard.id, dashboard.tabs?.length]);

  const activeTab = tabs.find(t => t.id === activeTabId);
  const widgets   = activeTab?.widgets ?? [];

  const patchTabs = (updater) => setTabs(prev => updater(prev));

  // ── Tab operations ──

  const handleAddTab = async () => {
    const title = `Tab ${tabs.length + 1}`;
    try {
      const tab = await dashApi.createTab(workspaceId, dashboard.id, { title });
      patchTabs(t => [...t, { ...tab, widgets: tab.widgets ?? [] }]);
      setActiveTabId(tab.id);
    } catch (e) { alert('Failed to create tab: ' + e.message); }
  };

  const handleRenameTab = async (tabId, title) => {
    try {
      await dashApi.updateTab(workspaceId, dashboard.id, tabId, { title });
      patchTabs(t => t.map(x => x.id === tabId ? { ...x, title } : x));
    } catch {}
  };

  const handleDeleteTab = async (tabId) => {
    setConfirmDel({ type: 'tab', id: tabId });
  };

  const confirmDeleteTab = async () => {
    const tabId = confirmDel?.id;
    setConfirmDel(null);
    try {
      await dashApi.deleteTab(workspaceId, dashboard.id, tabId);
      const next = tabs.filter(t => t.id !== tabId);
      setTabs(next);
      if (activeTabId === tabId) setActiveTabId(next[0]?.id ?? null);
    } catch (e) { alert('Failed to delete tab: ' + e.message); }
  };

  // ── Widget operations ──

  const handleAddWidget = async (type) => {
    setShowAdd(false);
    const sizeKey = type === 'chart' ? 'bar' : type;
    setEditing({
      isNew: true,
      widgetType: type,
      config: type === 'chart' ? { type: 'chart', chartSubtype: 'bar', query: { source: '', aggregations: [{ fn: 'count', alias: 'count' }], limit: 500 }, visualization: {} } :
              type === 'richtext' ? { type: 'richtext', content: '' } :
              { type: 'iframe', url: '' },
      position: { x: 0, y: 999, ...(DEFAULT_SIZES[sizeKey] || { w: 8, h: 4 }) },
    });
  };

  const handleEditWidget = (widget) => {
    setEditing({ ...widget, isNew: false });
  };

  const handleSaveWidget = async (draft) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const dto = {
        widgetType: draft.widgetType,
        title: draft.title,
        config: draft.config,
        position: draft.position,
        tabId: activeTabId,
      };
      if (draft.id) {
        const updated = await dashApi.updateWidget(workspaceId, dashboard.id, draft.id, dto);
        patchTabs(tabs => tabs.map(tab =>
          tab.id === activeTabId
            ? { ...tab, widgets: tab.widgets.map(w => w.id === draft.id ? { ...w, ...updated } : w) }
            : tab
        ));
      } else {
        const created = await dashApi.addWidget(workspaceId, dashboard.id, dto);
        patchTabs(tabs => tabs.map(tab =>
          tab.id === activeTabId
            ? { ...tab, widgets: [...tab.widgets, created] }
            : tab
        ));
      }
      setEditing(null);
    } catch (e) {
      setSaveError(e?.message || 'Failed to save widget');
    }
    setIsSaving(false);
  };

  const handleDeleteWidget = (widget) => {
    setConfirmDel({ type: 'widget', id: widget.id, name: widget.title });
  };

  const confirmDeleteWidget = async () => {
    const wId = confirmDel?.id;
    setConfirmDel(null);
    try {
      await dashApi.removeWidget(workspaceId, dashboard.id, wId);
      patchTabs(tabs => tabs.map(tab =>
        tab.id === activeTabId
          ? { ...tab, widgets: tab.widgets.filter(w => w.id !== wId) }
          : tab
      ));
    } catch (e) { alert('Failed to delete widget: ' + e.message); }
  };

  // ── Grid layout persistence (debounced) ──

  const handleLayoutChange = useCallback((layout) => {
    clearTimeout(layoutTimer.current);
    layoutTimer.current = setTimeout(async () => {
      const updates = layout
        .filter(item => {
          const w = widgets.find(x => x.id === item.i);
          if (!w) return false;
          const p = w.position || {};
          return p.x !== item.x || p.y !== item.y || p.w !== item.w || p.h !== item.h;
        })
        .map(item => ({
          id: item.i,
          position: { x: item.x, y: item.y, w: item.w, h: item.h },
        }));

      if (!updates.length) return;
      setLayoutBusy(true);
      try {
        await Promise.all(updates.map(u =>
          dashApi.updateWidget(workspaceId, dashboard.id, u.id, { position: u.position })
        ));
        patchTabs(tabs => tabs.map(tab =>
          tab.id === activeTabId
            ? { ...tab, widgets: tab.widgets.map(w => { const u = updates.find(x => x.id === w.id); return u ? { ...w, position: u.position } : w; }) }
            : tab
        ));
      } catch { /* layout persist failures are non-critical */ }
      setLayoutBusy(false);
    }, 800);
  }, [widgets, workspaceId, dashboard.id, activeTabId]);

  return (
    <div className="nx-aq-shell">
      {/* Header */}
      <div className="nx-aq-shell-head">
        <h1 className="nx-aq-dash-title">{dashboard.name}</h1>
        {dashboard.description && <span className="nx-aq-dash-desc">{dashboard.description}</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {layoutBusy && <Spinner size={14} />}
          <Btn variant="primary" size="sm" icon="Plus" onClick={() => setShowAdd(true)}>Add Widget</Btn>
        </div>
      </div>

      {/* Tab bar */}
      {tabs.length > 0 && (
        <DashboardTabBar tabs={tabs} activeTabId={activeTabId}
          onSwitch={setActiveTabId}
          onAdd={handleAddTab}
          onRename={handleRenameTab}
          onDelete={handleDeleteTab} />
      )}

      {/* Grid */}
      <div className="nx-aq-shell-body">
        <WidgetGrid
          widgets={widgets}
          workspaceId={workspaceId}
          dashboardId={dashboard.id}
          onEdit={handleEditWidget}
          onDelete={handleDeleteWidget}
          onLayoutChange={handleLayoutChange}
        />
      </div>

      {/* Modals */}
      {showAdd && <AddWidgetModal onSelect={handleAddWidget} onClose={() => setShowAdd(false)} />}

      {editing && (
        <WidgetEditor
          initial={editing}
          workspaceId={workspaceId}
          sources={sources}
          onSave={handleSaveWidget}
          onClose={() => { setEditing(null); setSaveError(null); }}
          isSaving={isSaving}
          saveError={saveError}
        />
      )}

      {confirmDel && (
        <ConfirmDialog
          title={confirmDel.type === 'tab' ? 'Delete Tab?' : 'Delete Widget?'}
          desc={confirmDel.type === 'tab'
            ? 'Widgets on this tab will move to the first remaining tab.'
            : `Delete "${confirmDel.name || 'this widget'}"?`}
          danger
          confirmLabel="Delete"
          onConfirm={confirmDel.type === 'tab' ? confirmDeleteTab : confirmDeleteWidget}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </div>
  );
};

// ─── Dashboard List Sidebar ───────────────────────────────────────────────────

const DashboardSidebar = ({ dashboards, activeDashId, onSelect, onCreate, onDelete }) => {
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await onCreate(newName.trim());
      setNewName('');
      setShowCreate(false);
    } finally { setCreating(false); }
  };

  return (
    <div className="nx-aq-sidebar">
      <div className="nx-aq-sidebar-head">
        <span className="nx-aq-sidebar-label">Dashboards</span>
        <button type="button" className="nx-iconbtn" onClick={() => setShowCreate(v => !v)} title="New dashboard">
          <NexusIcon name="Plus" size={14} />
        </button>
      </div>

      {showCreate && (
        <div className="nx-aq-sidebar-create">
          <input autoFocus className="nx-input" value={newName} onChange={e => setNewName(e.target.value)}
            placeholder="Dashboard name…"
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowCreate(false); }} />
          <Btn variant="primary" size="sm" onClick={handleCreate} disabled={creating || !newName.trim()}>
            {creating ? '…' : 'Create'}
          </Btn>
        </div>
      )}

      <div className="nx-aq-sidebar-list">
        {dashboards.map(d => (
          <button key={d.id} type="button"
            className={'nx-aq-sidebar-item' + (d.id === activeDashId ? ' is-active' : '')}
            onClick={() => onSelect(d.id)}>
            <NexusIcon name="LayoutDashboard" size={13} style={{ flexShrink: 0 }} />
            <span className="nx-aq-sidebar-item-name">{d.name}</span>
            {d.isDefault && <Pill tone="blue" size="sm">Default</Pill>}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Custom Dashboards View (Top-Level) ───────────────────────────────────────

const CustomDashboardsView = ({ workspaceId }) => {
  const [dashboards, setDashboards]     = useState([]);
  const [activeDashId, setActiveDashId] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dashApi.listDashboards(workspaceId);
      setDashboards(data);
      if (data.length) {
        const def = data.find(d => d.isDefault) || data[0];
        setActiveDashId(def.id);
      }
    } catch (e) {
      setError(e.message || 'Failed to load dashboards');
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (name) => {
    const d = await dashApi.createDashboard(workspaceId, { name, isDefault: dashboards.length === 0 });
    setDashboards(prev => [...prev, d]);
    setActiveDashId(d.id);
  };

  const activeDash = dashboards.find(d => d.id === activeDashId);

  if (loading) {
    return (
      <div className="nx-aq-loading">
        <Spinner size={24} />
        <span style={{ marginTop: 12, color: 'var(--nx-text-muted)' }}>Loading dashboards…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nx-aq-loading">
        <EmptyState icon="AlertCircle" title="Could not load dashboards" desc={error}
          action={<Btn variant="primary" onClick={load}>Retry</Btn>} />
      </div>
    );
  }

  return (
    <div className="nx-aq-root">
      <DashboardSidebar
        dashboards={dashboards}
        activeDashId={activeDashId}
        onSelect={setActiveDashId}
        onCreate={handleCreate}
        onDelete={() => {}}
      />

      <div className="nx-aq-main">
        {!dashboards.length ? (
          <div className="nx-aq-loading">
            <EmptyState
              icon="LayoutDashboard"
              title="No dashboards yet"
              desc="Create your first dashboard to start building analytics views."
              action={
                <Btn variant="primary" icon="Plus" onClick={() => handleCreate('My Dashboard')}>
                  Create Dashboard
                </Btn>
              }
            />
          </div>
        ) : activeDash ? (
          <DashboardShell
            key={activeDash.id}
            dashboard={activeDash}
            workspaceId={workspaceId}
            onChange={() => {}}
          />
        ) : (
          <div className="nx-aq-loading">
            <EmptyState icon="LayoutDashboard" title="Select a dashboard" />
          </div>
        )}
      </div>
    </div>
  );
};

window.CustomDashboardsView = CustomDashboardsView;
